import { useEffect, useState } from "react"

import { App, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import Button from "../../../../../Components/Button"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { modalConfig } from "../../../../../theme"
import { selectActiveProcessor, selectEnrichmentCatalog } from "../redux/configure.slice"
import { connectTool, disconnectTool, getEnrichmentTools } from "../redux/configure.thunks"

// Reuse the exact processor-card design so the Enrichment cards match the Integrate
// step's payment-processor cards.
import "../../../../IntegrationsPage/components/Card/styles.less"
import cardStyles from "./integrationCard.module.less"

const { Text, Title } = Typography

// The cards come from the backend catalog (GET enrichment/tools/), which lists only the
// tools this deployment can actually connect, so nothing here is a placeholder. The
// catalog carries no copy, so what each tool adds lives here, keyed by its id.
const TOOL_DESCRIPTIONS = {
  intercom: "Attach the cardholder's Intercom conversation to your evidence."
}

// Built-in enrichment rides along with a processor connection: listed only on that
// processor's step, always connected, with no Connect or Disconnect.
const BUILT_IN_TOOLS = [
  {
    id: "17track",
    name: "17Track",
    desc: "Tracks your Shopify deliveries so tracking scans and proof of delivery are attached to your evidence automatically.",
    onlyFor: "shopify",
    builtIn: true,
    connected: true
  }
]

// Query flags the OAuth callback appends when it sends the merchant back here.
const RETURN_PARAMS = ["enrichment_connected", "provider", "reason"]

// One enrichment tool card, same .integration-card design as the payment-processor
// cards on the Integrate step.
const EnrichmentCard = ({ tool, error, connecting, onConnect, onDisconnect }) => {
  const { t } = useTranslation()
  const logo = Images[`integration_${tool.id}`]
  const desc = TOOL_DESCRIPTIONS[tool.id] || tool.desc
  return (
    <Flex vertical gap={16} className="integration-card rcv-integrate-card">
      <Flex align="center" justify="space-between">
        {logo ? (
          <img
            src={logo}
            alt={tool.name}
            width={32}
            height={32}
            className="integration-card-logo"
          />
        ) : (
          <div className={cardStyles.intCardIcon}>
            <Icon type="integrations" style={{ fontSize: 20 }} />
          </div>
        )}
        {tool.needs_reconnect ? (
          <Flex gap={4} align="center" className="mark-beta">
            <Icon type="warning" style={{ fontSize: 20 }} />
            <Text style={{ color: "currentColor" }}>{t("Reconnect needed")}</Text>
          </Flex>
        ) : tool.connected ? (
          <Flex gap={4} align="center" className="mark-connected">
            <Icon type="check_mark" style={{ fontSize: 20 }} />
            <Text style={{ color: "currentColor" }}>{t("Connected")}</Text>
          </Flex>
        ) : null}
      </Flex>
      <div>
        <Title level={5} style={{ lineHeight: "22px", margin: "0 0 8px" }}>
          {t(tool.name, { defaultValue: tool.name })}
        </Title>
        {desc ? <Text type="secondary">{t(desc)}</Text> : null}
        {tool.connected && tool.connected_account ? (
          <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            {tool.connected_account}
          </Text>
        ) : null}
      </div>
      {tool.needs_reconnect ? (
        <Text type="secondary">
          {t(
            "_name_ stopped accepting our access, so nothing is attached to your evidence. Reconnect to resume.",
            { name: tool.name }
          )}
        </Text>
      ) : null}
      {error ? <Text type="danger">{error}</Text> : null}
      <Flex gap={12} vertical style={{ marginTop: "auto", width: "100%" }}>
        {tool.builtIn ? (
          <Flex gap={8} align="center" justify="center" style={{ minHeight: 40 }}>
            <Text type="secondary">{t("Included automatically, nothing to connect.")}</Text>
          </Flex>
        ) : (
          <>
            {tool.needs_reconnect || !tool.connected ? (
              <Button
                type={tool.needs_reconnect ? "primary" : "default"}
                size="large"
                className="flex center"
                icon={<Icon type="plugin_connect" style={{ fontSize: 20 }} />}
                loading={connecting}
                onClick={() => onConnect(tool)}
                text={tool.needs_reconnect ? t("Reconnect") : t("Connect")}
              />
            ) : null}
            {tool.connected || tool.needs_reconnect ? (
              <Button
                type="default"
                danger
                size="large"
                className="flex center"
                icon={<Icon type="circle_x" style={{ fontSize: 20 }} />}
                onClick={() => onDisconnect(tool)}
                text={t("Disconnect")}
              />
            ) : null}
          </>
        )}
      </Flex>
    </Flex>
  )
}

// Enrichment connections belong to the business account, not to the processor being
// configured: the same Intercom workspace serves Stripe and Shopify alike. Only the
// built-in cards depend on the active processor.
const EnrichmentTab = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { modal, message } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const activeProcessor = useSelector(selectActiveProcessor)
  const catalog = useSelector(selectEnrichmentCatalog)
  const toolConnect = useSelector((s) => s.disputesConfigure.toolConnect)
  // A failed OAuth round trip, reported by the callback's query flags: { provider, text }.
  const [returnError, setReturnError] = useState(null)

  const tools = catalog.tools || []
  // Right after the OAuth return the catalog may not have answered yet, so the slug stands in.
  const nameOf = (provider) =>
    tools.find((tool) => tool.id === provider)?.name ||
    `${provider.charAt(0).toUpperCase()}${provider.slice(1)}`

  // Coming back from the tool's consent screen: report the outcome once, then drop the
  // flags so a reload does not repeat it.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const outcome = params.get("enrichment_connected")

    if (outcome === null) return

    // The callback always names the provider; without one there is no card to report on.
    const provider = params.get("provider")
    const name = provider ? nameOf(provider) : null

    if (provider && outcome === "1") {
      setReturnError(null)
      message.success(t("_name_ connected", { name }))
    } else if (provider) {
      setReturnError({
        provider,
        text:
          params.get("reason") === "account_in_use"
            ? t("This _name_ workspace is already connected to another business.", { name })
            : t("Could not connect _name_. Please try again.", { name })
      })
    }

    RETURN_PARAMS.forEach((key) => params.delete(key))
    const search = params.toString()
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true })
    dispatch(getEnrichmentTools())
    // Runs per return, not per catalog change: nameOf only dresses up the message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const handleConnect = (tool) => {
    setReturnError(null)
    dispatch(connectTool({ slug: tool.id }))
  }

  const handleDisconnect = (tool) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: true,
      title: t("Disconnect _name_?", { name: tool.name }),
      content: t(
        "We'll stop attaching _name_ records to your evidence. You can reconnect any time.",
        { name: tool.name }
      ),
      okText: t("Disconnect"),
      okButtonProps: { ...modalConfig("exclamation").okButtonProps, danger: true },
      cancelText: t("Cancel"),
      getContainer: () => document.getElementById("main-layout"),
      onOk: () =>
        dispatch(disconnectTool({ slug: tool.id }))
          .unwrap()
          .then(() => message.success(t("_name_ disconnected", { name: tool.name })))
          .catch(() => {})
    })
  }

  const cards = [...tools, ...BUILT_IN_TOOLS.filter((tool) => tool.onlyFor === activeProcessor)]

  const errorFor = (tool) => {
    if (returnError?.provider === tool.id) return returnError.text
    if (toolConnect?.provider === tool.id && toolConnect.error) return t(toolConnect.error)
    return null
  }

  return (
    <div className={cardStyles.intCardgrid}>
      {cards.map((tool) => (
        <EnrichmentCard
          key={tool.id}
          tool={tool}
          error={errorFor(tool)}
          connecting={!!toolConnect?.loading && toolConnect.provider === tool.id}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
        />
      ))}
    </div>
  )
}

export default EnrichmentTab
